import type { WerfSystem } from '@/api/warehouse';

const pad = (n: number) => String(n).padStart(2, '0');

/** Local YYYY-MM-DD for a Date — timezone-safe (never uses toISOString, which shifts in CET/CEST). */
export function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today as a local YYYY-MM-DD string. */
export function todayLocalStr(): string {
  return toLocalDateStr(new Date());
}

/** Shift a YYYY-MM-DD string by `days` (positive = forward), staying in local time. */
export function shiftDateStr(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  return toLocalDateStr(date);
}

const WEEKDAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

/** "di 09/06" from a local YYYY-MM-DD string (no Date parsing pitfalls). */
export function formatDayLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${WEEKDAYS[date.getDay()]} ${pad(d)}/${pad(m)}`;
}

/** True if the given local YYYY-MM-DD string is today. */
export function isToday(dateStr: string): boolean {
  return dateStr === todayLocalStr();
}

export interface SystemPillStyle {
  /** Tailwind classes for the combined "systeem · m²" pill. */
  className: string;
}

// Brand-color rotation for system pills, matching the mockup. Keyed by a
// normalized system name; unknown systems fall through to a neutral style.
const SYSTEM_PILL_STYLES: Record<string, string> = {
  tacker: 'bg-td-blue/10 text-td-blue',
  infrezen: 'bg-td-green/15 text-td-green',
  staalnet: 'bg-td-purple/10 text-td-purple',
  betonnet: 'bg-td-magenta/10 text-td-magenta',
};

const NEUTRAL_PILL = 'bg-gray-100 text-td-muted';

/** Tailwind classes for a system pill, rotating brand colors by system name. */
export function systemPillClass(systeem: string): string {
  const key = systeem.trim().toLowerCase();
  return SYSTEM_PILL_STYLES[key] ?? NEUTRAL_PILL;
}

/** "Tacker · 172 m²" — capitalized system + rounded m². */
export function systemPillLabel(s: WerfSystem): string {
  const name = s.systeem ? s.systeem.charAt(0).toUpperCase() + s.systeem.slice(1) : 'Systeem ?';
  return `${name} · ${Math.round(s.m2)} m²`;
}

/** Two-letter uppercase initials from a name ("Oleg Carenco" → "OC"). */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Google Maps directions URL for an address string. */
export function mapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}
